import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import {
  AlertTriangle,
  Ban,
  Bell,
  BellOff,
  Bot,
  BotOff,
  CheckCheck,
  Inbox,
  MessageSquare,
  Phone,
  Search,
  SquarePen,
  User,
  Users,
  UserX,
  Volume2,
  VolumeX,
  Clock,
  RefreshCw,
  X,
} from 'lucide-react';
import { useContactComms } from '@/lib/contacts/contactComms';
import { useContactSmsSenderLines } from '@/hooks/contacts/useContactSmsSenderLines';
import { NewSmsMessage } from '@/components/inbox/NewSmsMessage';
import { useInboxes } from '@/lib/messaging/useInboxes';
import { useHasPermission } from '@/hooks/usePermissions';
import { ConversationList } from '@/components/inbox/ConversationList';
import { MessageThread } from '@/components/inbox/MessageThread';
import { MessageComposer } from '@/components/inbox/MessageComposer';
import { RemovedContactNotice } from '@/components/inbox/RemovedContactNotice';
import { VisitorContextPanel } from '@/components/messaging/VisitorContextPanel';
import {
  EmailMessageComposer,
  type EmailComposerSendPayload,
} from '@/components/inbox/EmailMessageComposer';
import { useMyProfile } from '@/hooks/useMyProfile';
import { useEmailAccounts } from '@/hooks/email/useEmailAccounts';
import {
  isSenderBlocked,
  useBlockEmailSender,
  useEmailIgnoreRules,
} from '@/hooks/email/useEmailIgnoreRules';
import {
  useInfiniteThreads,
  useThreads,
  useThread,
  useInfiniteThreadMessages,
  useSendMessage,
  useSendEmailReply,
  useMarkRead,
  useCloseThread,
  useReopenThread,
  useReassignThread,
  useSetThreadAiEnabled,
  useSetThreadSection,
} from '@/lib/messaging/hooks';
import { escalationLabel } from '@/lib/messaging/escalation';
import { useAuthUserId } from '@/stores/auth.store';
import { messagingApi } from '@/lib/messaging/api';
import {
  appendCachedMessage,
  flattenMessagePages,
  mapCachedMessages,
  type MessageCache,
} from '@/lib/messaging/messagePagination';
import { messagingKeys } from '@/lib/queryKeys';
import { Button } from '@/components/atoms/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import { cn } from '@/lib/utils';
import { useBrowserNotifications } from '@/hooks/notifications/useBrowserNotifications';
import { useDocumentTitleBadge } from '@/hooks/notifications/useDocumentTitleBadge';
import { useNotificationSound } from '@/hooks/notifications/useNotificationSound';
import {
  hasNewInbound,
  snapshotsByThreadId,
  totalUnreadCount,
  type ThreadSnapshot,
} from '@/lib/notifications/thread-diff';
import { useTenantPhoneSet } from '@/hooks/messaging/useTenantPhoneSet';
import { useActiveInboxThread } from '@/hooks/messaging/useInboxStream';
import { useThreadGenerating } from '@/stores/inbox.store';
import type {
  MediaAttachmentRef,
  MessageItem,
  ThreadDetail,
  ThreadListItem,
  ThreadStatus,
} from '@/lib/messaging/types';
import type { Attachment, Channel, Conversation, Message as FeMessage } from '@/types/conversation';
import { Flush } from '@/contexts/fullbleed';

// ─── Session-storage keys ─────────────────────────────────────────────────────

const PERMISSION_BANNER_DISMISS_KEY = 'notifi:inbox-permission-banner-dismissed';

// ─── Route ────────────────────────────────────────────────────────────────────

type View = 'all' | 'mine' | 'unread' | 'escalated' | 'closed' | 'other';

/**
 * Route search shape.
 */
type InboxSearch = {
  view: View;
  q: string;
  threadId: string | null;
  // The selected channel (Inbox) — filters the conversation list to a single
  // Sales/Support inbox. Absent = all channels the user can see.
  inboxId?: string;
};

export const Route = createFileRoute('/_dashboard/dashboard/inbox')({
  validateSearch: (search: Record<string, unknown>): InboxSearch => {
    return {
      view: (search.view as View) ?? 'all',
      q: String(search.q ?? ''),
      threadId: search.threadId ? String(search.threadId) : null,
      ...(search.inboxId ? { inboxId: String(search.inboxId) } : {}),
    };
  },
  component: InboxPage,
});

// ─── Mappers — messaging API → existing FE component shapes ──────────────────

function mapChannel(c: string): Channel {
  if (c === 'EMAIL') return 'email';
  if (c === 'FB_MESSENGER' || c === 'INSTAGRAM') return 'facebook';
  return 'sms';
}

function mapThreadToConversation(t: ThreadListItem): Conversation {
  const lastMessage = t.lastMessage;
  const isGroup = t.threadType === 'GROUP_MMS';
  const groupName =
    t.title?.trim() ||
    t.groupParticipants
      .slice(0, 3)
      .map((participant) => participant.displayName ?? participant.e164)
      .join(', ') ||
    'Messaging group';
  // Prefer the thread-level `channel` (server derives it from the
  // last-message channel with `phoneNumberId === EMAIL_SENTINEL` as
  // fallback, so empty email threads still resolve as 'email').
  // Older API builds that haven't rolled out the field fall back to
  // the legacy lastMessage-only path, defaulting to SMS.
  const threadChannel: string | undefined = t.channel ?? lastMessage?.channel;
  return {
    id: t.id,
    channel: threadChannel ? mapChannel(threadChannel) : 'sms',
    contact: {
      id: t.customer?.id ?? `thread:${t.id}`,
      name: isGroup ? groupName : (t.senderIdentifier ?? t.customer?.displayName ?? '(unknown)'),
      phone: isGroup
        ? `${t.groupParticipants.length} participants`
        : (t.customer?.primaryPhone ?? undefined),
      email: t.customer?.primaryEmail ?? undefined,
      isDeleted: t.customer?.isDeleted ?? false,
    },
    lastMessage: lastMessage
      ? {
          id: lastMessage.id,
          body: lastMessage.body,
          direction: lastMessage.direction === 'OUTBOUND' ? 'outbound' : 'inbound',
          createdAt: lastMessage.sentAt ?? t.lastMessageAt,
        }
      : undefined,
    unreadCount: t.unreadCount,
    status: t.status === 'CLOSED' ? 'closed' : 'open',
    createdAt: t.lastMessageAt,
    updatedAt: t.lastMessageAt,
    // Carried through so the row can flag "Iris escalated this" — the
    // list used to drop every AI field here, which is why an escalation was
    // invisible until the operator opened the thread.
    escalation: t.escalation ?? null,
  };
}

function mapMessage(m: MessageItem, clientKeyForId?: Map<string, string>): FeMessage {
  // `_clientKey` is the bubble's stable React identity across the optimistic
  // → real-id reconciliation. For sent-by-us messages we keep a side map of
  // `realId → optimisticId` on the active-thread component (so refetches
  // that drop in the server's payload still hit the same key). For every
  // other message (inbound, historical, sent by another user) the key is
  // just the real id — no mapping, no behaviour change.
  const clientKey = clientKeyForId?.get(m.id) ?? m.id;
  return {
    id: m.id,
    _clientKey: clientKey,
    conversationId: m.threadId,
    body: m.body,
    direction: m.direction === 'OUTBOUND' ? 'outbound' : 'inbound',
    channel: mapChannel(m.channel),
    status:
      m.deliveryStatus === 'FAILED' ||
      m.deliveryStatus === 'REJECTED' ||
      m.deliveryStatus === 'UNDELIVERED'
        ? 'failed'
        : m.deliveryStatus === 'READ'
          ? 'read'
          : m.deliveryStatus === 'DELIVERED'
            ? 'delivered'
            : m.deliveryStatus === 'SENT'
              ? 'sent'
              : 'pending',
    // SMS/MMS media: the backend returns signed display URLs on
    // `attachments`. Map to the FE `Attachment` shape, dropping any entry
    // without a usable URL and keying by URL (stable across refetch).
    attachments: (m.attachments ?? [])
      .filter((a): a is typeof a & { url: string } => Boolean(a?.url))
      .map((a) => ({
        id: a.url,
        url: a.url,
        mimeType: a.mimeType ?? 'application/octet-stream',
        filename: a.filename ?? 'attachment',
        size: a.size ?? 0,
      })),
    createdAt: m.sentAt ?? m.createdAt,
    updatedAt: m.sentAt ?? m.createdAt,
    emailSubject: m.subject ?? null,
    emailHtml: m.html ?? null,
    emailAttachments: (m.emailAttachments ?? []).map((a) => ({
      messageId: m.id,
      providerAttachmentId: a.providerAttachmentId,
      filename: a.filename,
      mimeType: a.mimeType,
      size: a.size,
      url: a.url ?? null,
    })),
    aiAgent: m.aiAgent ?? null,
    senderName: m.senderName ?? null,
    groupDeliverySummary:
      m.groupDeliveries && m.groupDeliveries.length > 0
        ? {
            total: m.groupDeliveries.length,
            delivered: m.groupDeliveries.filter((delivery) => delivery.status === 'DELIVERED')
              .length,
            failed: m.groupDeliveries.filter(
              (delivery) => delivery.status === 'FAILED' || delivery.status === 'UNDELIVERED',
            ).length,
          }
        : undefined,
  };
}

// ─── View → API filter helpers ───────────────────────────────────────────────

interface ViewFilter {
  section?: 'INBOX' | 'OTHER';
  status?: ThreadStatus[];
  unreadOnly?: boolean;
  escalatedOnly?: boolean;
  assignedUserId?: string;
}

// CLOSED threads are isolated to the 'closed' tab; every other view
// filters them out so the operator's main inbox isn't crowded by
// finished conversations. The thread header already exposes a Reopen
// action when status === CLOSED, which moves it back to the active
// views (OPEN/SNOOZED/RESTRICTED).
const NON_CLOSED_STATUSES: ThreadStatus[] = ['OPEN', 'SNOOZED', 'RESTRICTED'];

function viewToFilter(view: View): ViewFilter {
  if (view === 'other') return { section: 'OTHER', status: [...NON_CLOSED_STATUSES, 'CLOSED'] };
  if (view === 'closed') return { status: ['CLOSED'] };
  const base: ViewFilter = { status: NON_CLOSED_STATUSES };
  if (view === 'mine') return { ...base, assignedUserId: 'me' };
  if (view === 'unread') return { ...base, unreadOnly: true };
  // The section for conversations Iris handed to a human that nobody has
  // picked up yet. The backend queues these oldest-escalation first.
  if (view === 'escalated') return { ...base, escalatedOnly: true };
  return base;
}

// ─── Page ─────────────────────────────────────────────────────────────────────

function InboxPage() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const [searchInput, setSearchInput] = useState(search.q);
  const activeId = search.threadId;
  const [composeInboxId, setComposeInboxId] = useState<string | null>(null);
  const inboxes = useInboxes();
  const canCreateMessage = useHasPermission('messages:create');
  const selectedInbox = inboxes.data?.find((inbox) => inbox.id === search.inboxId);
  const canCompose =
    canCreateMessage && selectedInbox?.kind === 'PHONE' && selectedInbox.status === 'ACTIVE';
  const composing = canCompose && composeInboxId === selectedInbox.id && !activeId;

  useEffect(() => {
    setComposeInboxId(null);
  }, [search.inboxId]);

  // ── Real-time stream ───────────────────────────────────────────────────────
  // The connection itself belongs to the dashboard layout now — it has to stay
  // open on every screen a call can be answered from, not just this one. All
  // this screen contributes is which thread is open, which is the only thing
  // the stream cannot work out for itself. Polling (defined in
  // `lib/messaging/hooks.ts`) is still configured at a slow cadence as a
  // fallback for environments where the stream can't connect.
  useActiveInboxThread(activeId);

  // ── Threads list ────────────────────────────────────────────────────────────
  const viewFilter = viewToFilter(search.view);
  const threadsQuery = useInfiniteThreads({
    ...viewFilter,
    search: search.q || undefined,
    inboxId: search.inboxId,
  });

  const threads = useMemo(
    () => threadsQuery.data?.pages.flatMap((p) => p.items) ?? [],
    [threadsQuery.data],
  );
  const conversations = useMemo(() => threads.map(mapThreadToConversation), [threads]);

  // Tenant's own phone numbers. Used to flag intra-account conversations
  // (where the sender is one of OUR Telnyx lines) with an "Internal"
  // badge so operators can distinguish platform-admin tests from real
  // customer messages. Reuses the same react-query cache the composer
  // already populates — no duplicate HTTP request.
  const tenantPhoneSet = useTenantPhoneSet();

  // ── Notification plumbing ───────────────────────────────────────────────────
  const { permission, canRequest, requestPermission, notify } = useBrowserNotifications();
  const sound = useNotificationSound();

  // Total unread used in two places: header label and tab-title badge.
  // Sum across all *visible* threads except CLOSED (so badge follows the
  // active view rather than the whole inbox).
  const unreadTotal = useMemo(() => totalUnreadCount(threads), [threads]);
  useDocumentTitleBadge(unreadTotal, 'Notifi');

  // How many conversations are waiting for a person. A one-row page is enough:
  // the count comes from the paginated `total`, and the query key sits under
  // `messagingKeys.threads()` so the SSE stream refreshes it on every
  // `thread.escalated` / resume like the list itself.
  const escalatedQuery = useThreads({
    status: NON_CLOSED_STATUSES,
    escalatedOnly: true,
    pageSize: 1,
  });
  const escalatedTotal = escalatedQuery.data?.total ?? 0;
  const otherQuery = useThreads({
    section: 'OTHER',
    status: [...NON_CLOSED_STATUSES, 'CLOSED'],
    unreadOnly: true,
    inboxId: search.inboxId,
    pageSize: 1,
  });
  const otherUnread = otherQuery.data?.total ?? 0;

  // ── Detect new INBOUND arrivals between polls and fire notifications ───────
  // `lastSnapshotRef` holds the previous poll's per-thread snapshot map.
  // `hasSeenFirstPollRef` guards the cold-start: on the very first poll we
  // record state but DO NOT fire — otherwise we'd ding for every pre-existing
  // unread thread the moment the page mounts.
  const lastSnapshotRef = useRef<Map<string, ThreadSnapshot> | null>(null);
  const hasSeenFirstPollRef = useRef(false);

  // Stable navigate function for notification click handlers — captures the
  // current `navigate` instance without invalidating on every keystroke.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  }, [navigate]);

  useEffect(() => {
    if (!threadsQuery.data) return;
    const currentThreads = threads;

    if (!hasSeenFirstPollRef.current) {
      hasSeenFirstPollRef.current = true;
      lastSnapshotRef.current = snapshotsByThreadId(currentThreads);
      return;
    }

    const prevSnapshot = lastSnapshotRef.current ?? new Map<string, ThreadSnapshot>();
    let anyNew = false;
    for (const t of currentThreads) {
      if (t.section && t.section !== 'INBOX') continue;
      if (!hasNewInbound(t, prevSnapshot.get(t.id))) continue;
      anyNew = true;
      const body = t.lastMessage?.body ?? 'New message';
      notify({
        title:
          t.threadType === 'GROUP_MMS'
            ? (t.title ?? 'Messaging group')
            : (t.customer?.displayName ?? 'New message'),
        body: body.length > 140 ? `${body.slice(0, 137)}…` : body,
        threadId: t.id,
        onClick: () => {
          void navigateRef.current({
            search: (prev) => ({ ...prev, threadId: t.id }),
            replace: false,
          });
        },
      });
    }
    if (anyNew) sound.play();
    lastSnapshotRef.current = snapshotsByThreadId(currentThreads);
  }, [threadsQuery.data, threads, notify, sound]);

  // ── Permission banner ──────────────────────────────────────────────────────
  const [bannerDismissed, setBannerDismissed] = useState<boolean>(() => {
    if (typeof window === 'undefined') return false;
    try {
      return window.sessionStorage.getItem(PERMISSION_BANNER_DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });
  const dismissBanner = useCallback(() => {
    setBannerDismissed(true);
    try {
      window.sessionStorage.setItem(PERMISSION_BANNER_DISMISS_KEY, '1');
    } catch {
      // Ignored — banner just won't persist its dismissal.
    }
  }, []);
  const handleEnableNotifications = useCallback(async () => {
    const next = await requestPermission();
    if (next === 'granted') {
      toast.success('Desktop notifications enabled.');
    } else if (next === 'denied') {
      toast.error('Notifications blocked. You can re-enable them in your browser settings.');
    }
    dismissBanner();
  }, [requestPermission, dismissBanner]);
  const showPermissionBanner = canRequest && !bannerDismissed;

  // ── Navigation helpers ──────────────────────────────────────────────────────
  const handleSelectThread = useCallback(
    (id: string) => {
      setComposeInboxId(null);
      void navigate({
        search: (prev) => ({ ...prev, threadId: id }),
        replace: true,
      });
    },
    [navigate],
  );

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);
      void navigate({
        search: (prev) => ({ ...prev, q: value }),
        replace: true,
      });
    },
    [navigate],
  );

  // Channel selection lives in the left nav (the Inbox section drives
  // `?inboxId=…`). The message views live in this page's toolbar.
  const handleViewChange = useCallback(
    (view: View) => {
      setComposeInboxId(null);
      void navigate({
        search: (prev) => ({ ...prev, view, threadId: null }),
        replace: true,
      });
    },
    [navigate],
  );

  // Flush, not a hand-sized box (same fix as Call logs): the old
  // `h-[calc(100vh-var(--header-height,64px))] -m-6` guessed the card's height
  // and ignored its margin and the real header height, so the page ran past
  // the fold and a long email thread scrolled the whole page — toolbar and
  // all — instead of only the message pane.
  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
      <Flush />
      {/* Toolbar — message views + Calls on the left (the channel filter lives
          in the left nav now), sound + notification status on the right. */}
      <div className="flex h-11 shrink-0 items-center gap-1 border-b px-2">
        <div className="flex items-center gap-0.5">
          <ToolbarPill
            icon={<Inbox className="h-3.5 w-3.5" />}
            label="All"
            active={search.view === 'all'}
            onClick={() => handleViewChange('all')}
          />
          <ToolbarPill
            icon={<User className="h-3.5 w-3.5" />}
            label="Assigned to me"
            active={search.view === 'mine'}
            onClick={() => handleViewChange('mine')}
          />
          <ToolbarPill
            icon={<MessageSquare className="h-3.5 w-3.5" />}
            label="Unread"
            active={search.view === 'unread'}
            onClick={() => handleViewChange('unread')}
          />
          <ToolbarPill
            icon={<AlertTriangle className="h-3.5 w-3.5" />}
            label="Escalated"
            active={search.view === 'escalated'}
            onClick={() => handleViewChange('escalated')}
            count={escalatedTotal}
            attention
          />
          <ToolbarPill
            icon={<CheckCheck className="h-3.5 w-3.5" />}
            label="Closed"
            active={search.view === 'closed'}
            onClick={() => handleViewChange('closed')}
          />
          <ToolbarPill
            icon={<Inbox className="h-3.5 w-3.5" />}
            label="Other"
            active={search.view === 'other'}
            onClick={() => handleViewChange('other')}
            count={otherUnread}
          />
        </div>
        <div className="ml-auto flex items-center gap-2 text-muted-foreground">
          {canCompose && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                setComposeInboxId(selectedInbox.id);
                void navigate({ search: (prev) => ({ ...prev, threadId: null }), replace: true });
              }}
            >
              <SquarePen className="mr-1.5 h-3.5 w-3.5" />
              New message
            </Button>
          )}
          <button
            type="button"
            onClick={() => sound.setEnabled(!sound.enabled)}
            className="rounded-md p-1.5 transition-colors hover:bg-muted hover:text-foreground"
            aria-pressed={sound.enabled}
            title={sound.enabled ? 'Mute new-message sound' : 'Play a sound on new messages'}
          >
            {sound.enabled ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          {permission === 'granted' && (
            <span className="p-1.5" title="Desktop notifications are enabled">
              <Bell className="h-4 w-4" />
            </span>
          )}
          {permission === 'denied' && (
            <span className="p-1.5" title="Notifications are blocked at the browser level">
              <BellOff className="h-4 w-4" />
            </span>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="flex min-h-0 flex-1">
        {/* Conversation list column */}
        <div className="flex w-96 shrink-0 flex-col border-r">
          {showPermissionBanner && (
            <PermissionBanner onEnable={handleEnableNotifications} onDismiss={dismissBanner} />
          )}
          <div
            className="flex-1 min-h-0"
            role="region"
            aria-label="Conversations"
            aria-live="polite"
            aria-relevant="additions"
          >
            <ConversationList
              conversations={conversations}
              isLoading={threadsQuery.isLoading}
              activeId={activeId}
              search={searchInput}
              onSelect={handleSelectThread}
              onSearchChange={handleSearchChange}
              tenantPhoneSet={tenantPhoneSet}
              hasNextPage={threadsQuery.hasNextPage}
              isFetchingNextPage={threadsQuery.isFetchingNextPage}
              onLoadMore={() => void threadsQuery.fetchNextPage()}
            />
          </div>
        </div>

        {/* Thread pane */}
        <div className="flex flex-1 flex-col min-w-0">
          {composing ? (
            <NewSmsMessage
              key={selectedInbox.id}
              inbox={selectedInbox}
              onCancel={() => setComposeInboxId(null)}
              onSent={(threadId) => {
                setComposeInboxId(null);
                setSearchInput('');
                void navigate({
                  search: (prev) => ({ ...prev, view: 'all', q: '', threadId }),
                  replace: true,
                });
              }}
            />
          ) : activeId ? (
            <ActiveThread key={activeId} threadId={activeId} />
          ) : (
            <EmptyState />
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Toolbar pill (message view / calls selector) ─────────────────────────────

function ToolbarPill({
  icon,
  label,
  active,
  onClick,
  count,
  attention,
}: {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
  /** Optional count badge (hidden at 0). */
  count?: number;
  /** Colour the pill red while `count` is non-zero — "act on this". */
  attention?: boolean;
}) {
  const hasCount = typeof count === 'number' && count > 0;
  const alarmed = Boolean(attention) && hasCount;
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex items-center gap-1.5 rounded-md px-2 py-1 text-xs transition-colors',
        active
          ? alarmed
            ? 'bg-red-100 text-red-800 font-medium'
            : 'bg-primary/10 text-primary font-medium'
          : alarmed
            ? 'text-red-700 hover:bg-red-50'
            : 'text-muted-foreground hover:bg-muted hover:text-foreground',
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
      {hasCount && (
        <span
          className={cn(
            'inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[10px] font-semibold tabular-nums',
            alarmed ? 'bg-red-600 text-white' : 'bg-primary text-primary-foreground',
          )}
        >
          {count > 99 ? '99+' : count}
        </span>
      )}
    </button>
  );
}

// ─── Escalation banner ────────────────────────────────────────────────────────

/**
 * Shown at the top of a thread Iris handed to a human and nobody has resumed.
 * Says why and since when, and offers the two things an operator does with
 * it: claim it (assign to me, so a colleague sees it is taken) and, once the
 * customer is looked after, hand the conversation back to Iris.
 */
function EscalationBanner({
  escalation,
  assignedToMe,
  canClaim,
  claimPending,
  resumePending,
  onClaim,
  onResume,
}: {
  escalation: NonNullable<ThreadDetail['escalation']>;
  assignedToMe: boolean;
  canClaim: boolean;
  claimPending: boolean;
  resumePending: boolean;
  onClaim: () => void;
  onResume: () => void;
}) {
  const since = new Date(escalation.at);
  const sinceLabel = !isNaN(since.getTime())
    ? formatDistanceToNow(since, { addSuffix: true })
    : null;
  return (
    <div
      role="status"
      className="flex shrink-0 flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-red-200 bg-red-50 px-4 py-2 text-xs text-red-900"
    >
      <AlertTriangle className="h-4 w-4 shrink-0 text-red-600" aria-hidden />
      <div className="min-w-0 flex-1">
        <span className="font-semibold">
          Iris escalated this conversation{sinceLabel ? ` ${sinceLabel}` : ''}.
        </span>{' '}
        <span>
          {escalationLabel(escalation.trigger)}
          {escalation.detail ? ` — ${escalation.detail}` : ''}
        </span>
      </div>
      <div className="flex shrink-0 items-center gap-1.5">
        {assignedToMe ? (
          <span className="rounded-sm bg-red-100 px-1.5 py-0.5 font-medium">
            You're handling this
          </span>
        ) : (
          canClaim && (
            <Button size="sm" variant="outline" onClick={onClaim} disabled={claimPending}>
              Claim
            </Button>
          )
        )}
        <Button
          size="sm"
          variant="outline"
          onClick={onResume}
          disabled={resumePending}
          title="Hand the conversation back to Iris. It leaves the Escalated section."
        >
          <Bot className="mr-1.5 h-3.5 w-3.5" />
          Resume Iris
        </Button>
      </div>
    </div>
  );
}

// ─── Active thread pane ───────────────────────────────────────────────────────

function ActiveThread({ threadId }: { threadId: string }) {
  const queryClient = useQueryClient();
  const navigate = Route.useNavigate();
  const threadQuery = useThread(threadId);
  const messagesQuery = useInfiniteThreadMessages(threadId);
  const sendMessage = useSendMessage(threadId);
  const sendEmailReply = useSendEmailReply(threadId);
  const markRead = useMarkRead(threadId);
  const closeThread = useCloseThread(threadId);
  const reopenThread = useReopenThread(threadId);
  const setThreadAi = useSetThreadAiEnabled(threadId);
  const setThreadSection = useSetThreadSection(threadId);
  const reassignThread = useReassignThread(threadId);
  const { callNumber } = useContactComms();
  const currentUserId = useAuthUserId();
  // True while Iris is composing a reply for this thread (driven by the
  // `ai.generating` SSE event) — renders the typing indicator in the thread.
  const isBotThinking = useThreadGenerating(threadId);
  // Ignore-sender plumbing for the "Block sender" button in the email
  // thread header — list is cached app-wide so we can detect "already
  // blocked" without a per-thread refetch.
  const blockSender = useBlockEmailSender();
  const ignoreRulesQuery = useEmailIgnoreRules();
  const ignoreRules = ignoreRulesQuery.data ?? [];
  // Current user's stored signature — fallback when the active mailbox
  // doesn't have its own signature configured. Cached app-wide so this
  // doesn't fire a refetch per active thread.
  const myProfileQuery = useMyProfile();
  const myProfile = myProfileQuery.data ?? null;
  // Connected mailboxes — the composer reads the active mailbox's
  // signature off this cache (signature is now per-mailbox; user-level
  // signature stays as a fallback for mailboxes without their own).
  const emailAccountsQuery = useEmailAccounts();
  const emailAccounts = emailAccountsQuery.data ?? [];
  const thread = threadQuery.data?.thread;
  const isGroup = thread?.threadType === 'GROUP_MMS';

  // The send mutation supplies the thread's bound `phoneNumberId` —
  // the server enforces `thread.phoneNumberId === input.phoneNumberId`
  // at the API boundary, so the FE just reads it off the thread detail
  // and round-trips it back. The previous "default-sender pick with
  // contact-phone exclusion" heuristic is gone: it was a band-aid for
  // the missing model-level binding (intra-account self-sends).
  const threadPhoneNumberId = threadQuery.data?.thread.phoneNumberId ?? null;

  // Tenant's own phone numbers — used to flag the header when the
  // conversation's customer is itself one of our Telnyx lines
  // (intra-account thread). Shares the same react-query cache the
  // composer already populates; no duplicate HTTP request.
  const tenantPhoneSet = useTenantPhoneSet();

  // Mark thread as read when opened. Re-fires whenever a new message arrives
  // (unreadCount > 0) so the badge clears via the next poll.
  useEffect(() => {
    if (threadQuery.data?.thread.unreadCount && threadQuery.data.thread.unreadCount > 0) {
      markRead.mutate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId, threadQuery.data?.thread.unreadCount]);

  // Stable React keys for messages we sent optimistically.
  //
  // Background: `handleSend` inserts a temp message with `id: opt-<ts>` into
  // the messages cache, then — after the server replies — rewrites that row
  // in place so its `id` becomes the real UUID. The cache MUST hold the real
  // id so subsequent SSE events / status updates / delete operations can find
  // the row by id. But MessageBubble uses `id` as its React key, so the swap
  // unmounts the optimistic bubble and mounts a fresh one for the real id —
  // framer-motion replays the bubble's `initial` animation on that mount and
  // the user sees the "blink" they reported.
  //
  // Fix: keep a side map from `realId → optimisticId` here. `mapMessage`
  // stamps `_clientKey = map.get(id) ?? id` on every Message, and the bubble
  // keys on that. Reconciliation populates the map BEFORE rewriting the
  // cache, so by the time React sees the new id the clientKey is already
  // pointing back at the optimistic id — same React identity, no remount,
  // no replay. Refetches that arrive later carry the real id; the map still
  // resolves to the optimistic clientKey, so they don't trigger a remount
  // either. The map lives for the lifetime of the open thread and is reset
  // when the user switches threads (because `ActiveThread` is keyed by
  // threadId at the page level and unmounts on switch).
  const clientKeyForIdRef = useRef<Map<string, string>>(new Map());
  // Staged MMS media refs (upload s3Key → full ref), consumed on the next send.
  const uploadedMediaRef = useRef<Map<string, MediaAttachmentRef>>(new Map());

  const messageItems = useMemo<MessageItem[]>(() => {
    if (!messagesQuery.data) return [];
    // Page 1 is the newest slice. Put older loaded pages first, while keeping
    // the API's chronological order inside every page.
    return flattenMessagePages(messagesQuery.data);
  }, [messagesQuery.data]);

  const messages = useMemo<FeMessage[]>(
    () => messageItems.map((message) => mapMessage(message, clientKeyForIdRef.current)),
    [messageItems],
  );

  const handleSend = useCallback(
    async (body: string, attachmentIds: string[] = []) => {
      const sendingWebChat =
        (threadQuery.data?.thread.channel ?? threadQuery.data?.thread.lastMessage?.channel) ===
        'WEB_CHAT';
      // Web-chat threads have no sending number — delivery is the realtime push
      // to the embedded widget, so a phone number is neither required nor used.
      if (!sendingWebChat && !threadPhoneNumberId) {
        toast.error('No active phone number available to send from.');
        return;
      }
      // Map the composer's attachment ids (our upload s3Keys) back to the
      // full media refs the send endpoint expects.
      const media = attachmentIds
        .map((id) => uploadedMediaRef.current.get(id))
        .filter((m): m is NonNullable<typeof m> => Boolean(m));
      const optimisticId = `opt-${Date.now()}`;
      const optimisticMsg: FeMessage = {
        id: optimisticId,
        conversationId: threadId,
        body,
        direction: 'outbound',
        channel: 'sms',
        status: 'pending',
        attachments: [],
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
        _optimistic: true,
      };

      // Inject the optimistic message into the messages cache by rewriting
      // every cached key that matches this thread's messages prefix.
      queryClient.setQueriesData<MessageCache>(
        { queryKey: [...messagingKeys.thread(threadId), 'messages'] },
        (old) => {
          if (!old) return old;
          // APPEND, don't prepend — the API returns messages oldest-first
          // (the thread renders ASC) so the bubble should sit at the END
          // of the array to appear at the BOTTOM of the thread. Prepending
          // it landed the optimistic bubble at the top and then "jumped"
          // to the bottom once the server ack triggered a refetch.
          return appendCachedMessage(old, {
            id: optimisticId,
            tenantId: '',
            threadId,
            customerId: isGroup ? null : '',
            channel: sendingWebChat ? 'WEB_CHAT' : isGroup ? 'MMS' : 'SMS',
            direction: 'OUTBOUND',
            sourceIdentifier: '',
            fromNumberId: threadPhoneNumberId,
            senderUserId: null,
            body,
            attachments: null,
            externalMediaUrl: null,
            deliveryStatus: 'QUEUED',
            errorCode: null,
            sentAt: optimisticMsg.createdAt,
            deliveredAt: null,
            readAt: null,
            createdAt: optimisticMsg.createdAt,
          });
        },
      );

      try {
        const result = await sendMessage.mutateAsync(
          sendingWebChat
            ? { body }
            : {
                phoneNumberId: threadPhoneNumberId ?? undefined,
                body,
                ...(media.length > 0 ? { media } : {}),
              },
        );
        // Sent — drop the staged refs so a later send doesn't re-attach them.
        for (const id of attachmentIds) uploadedMediaRef.current.delete(id);
        // Two-part reconciliation:
        //   1. Record `realId → optimisticId` in the clientKey map BEFORE we
        //      mutate the cache, so the very next render (which sees the
        //      swapped id) still resolves to the original optimistic
        //      clientKey via `mapMessage`. Without this, the React key on
        //      the bubble flips from `opt-<ts>` to the real UUID, the
        //      `<motion.div>` is unmounted + remounted, and framer-motion
        //      plays its `initial: { opacity: 0, y: 8 }` animation again —
        //      that's the "blink" the user sees ~1s after Send.
        //   2. Rewrite the optimistic row in place so the cache holds the
        //      real id (later SSE events / status updates / refetches all
        //      lookup by id). The previous attempt to fix the blink stopped
        //      at (2); it patched the id in place but left the React key
        //      tied to that id, so the optimistic bubble was still
        //      unmount/remounted — the comment was right about the symptom
        //      and wrong about why patching alone fixes it.
        clientKeyForIdRef.current.set(result.messageId, optimisticId);
        queryClient.setQueriesData<MessageCache>(
          { queryKey: [...messagingKeys.thread(threadId), 'messages'] },
          (old) =>
            old
              ? mapCachedMessages(old, (m) =>
                  m.id === optimisticId
                    ? { ...m, id: result.messageId, deliveryStatus: 'SENT' }
                    : m,
                )
              : old,
        );
      } catch {
        toast.error('Failed to send message. Please try again.');
      }
    },
    [threadPhoneNumberId, threadId, queryClient, sendMessage, threadQuery.data, isGroup],
  );

  // Upload an MMS media file to our bucket and stage its ref for the next
  // send. The composer works in terms of `Attachment { id, url, ... }`, so we
  // key the staged ref by the upload's s3Key (used as the attachment id) and a
  // local object URL for the preview thumbnail.
  const handleUpload = useCallback(async (file: File): Promise<Attachment> => {
    const ref = await messagingApi.threads.uploadAttachment(file);
    uploadedMediaRef.current.set(ref.s3Key, ref);
    return {
      id: ref.s3Key,
      url: URL.createObjectURL(file),
      mimeType: ref.mimeType ?? file.type,
      filename: ref.filename ?? file.name,
      size: ref.size ?? file.size,
    };
  }, []);

  // Prefer the right-pane card's customer (richer fields like companyName)
  // but fall back to the thread's embedded summary when the card omits it.
  // BOTH can degrade independently: the card returns `customer: null`
  // when the Contact row no longer exists (admin mass-deleted contacts
  // → orphan threads), while the thread row's customer summary degrades
  // to `displayName: '(unknown)'` from the same join.
  const customer = threadQuery.data?.customerContext?.customer ?? thread?.customer ?? null;
  const receiveOnly = Boolean(thread?.senderIdentifier);
  // True once the API has resolved AND told us the contact row is gone.
  // `customerContext.customer === null` is the authoritative signal from
  // the backend (see InboxQueryService.getCustomerContext). Gating on
  // `threadQuery.data` keeps the empty state from flashing while the
  // thread detail is still in-flight on first mount.
  const isContactDeleted = Boolean(
    threadQuery.data && !isGroup && threadQuery.data.customerContext?.customer === null,
  );
  // Soft-deleted (archived) contact: the row still resolves, so we keep its
  // identity and the click-through (the detail page lets the operator
  // restore it) but flag it with a "Deleted" badge. Distinct from the
  // hard-delete/orphan case above where `customer` is null entirely.
  const isContactSoftDeleted = Boolean(customer?.isDeleted) && !isContactDeleted;

  const isClosed = thread?.status === 'CLOSED';

  // ── Email reply context ────────────────────────────────────────────────
  //
  // For an email thread, we send replies through the connected mailbox
  // (`POST /messaging/email/accounts/:channelAccountId/messages`) rather
  // than the SMS endpoint. The mailbox, subject, and recipient are read
  // off the most-recent email message on this thread:
  //   - channelAccountId: which inbox the thread belongs to
  //   - subject: prefix with "Re: " unless already present
  //   - to: customer's primaryEmail (falls back to the sender's address
  //     from the last inbound message — `sourceIdentifier` carries the
  //     other side of the conversation regardless of direction)
  //
  // If we don't have enough context to reply (no email message yet, or
  // no mailbox id), the composer renders disabled with a brief reason.
  // Prefer the thread-level `channel` discriminator from the backend
  // (derived from last-message-channel + phoneNumberId sentinel). Falls
  // back to the last-message channel for older API builds. Empty email
  // threads still resolve as EMAIL because the backend keys off the
  // EMAIL_SENTINEL phoneNumberId when no last-message preview exists.
  const isEmailThread = (thread?.channel ?? thread?.lastMessage?.channel) === 'EMAIL';
  const isWebChat = (thread?.channel ?? thread?.lastMessage?.channel) === 'WEB_CHAT';
  const senderLines = useContactSmsSenderLines(
    customer?.id ?? '',
    Boolean(customer?.id) && !isGroup && !isEmailThread && !isWebChat,
  );
  const changeSenderLine = async (phoneNumberId: string) => {
    if (!customer?.id || phoneNumberId === threadPhoneNumberId) return;
    try {
      // A thread belongs to one sender line. Open that line's conversation so
      // replies return to the same thread the operator is composing in.
      const { threadId: nextThreadId } = await messagingApi.threads.openForCustomer(
        customer.id,
        phoneNumberId,
      );
      void navigate({
        to: '/dashboard/inbox',
        search: { view: 'all', q: '', threadId: nextThreadId },
      });
    } catch {
      toast.error('Could not switch sending number.');
    }
  };
  const latestEmailMessage = useMemo(() => {
    if (!isEmailThread) return null;
    for (let i = messageItems.length - 1; i >= 0; i -= 1) {
      const m = messageItems[i];
      if (m && m.channel === 'EMAIL') return m;
    }
    return null;
  }, [isEmailThread, messageItems]);

  // Fallback identity for the orphan-thread case. When the customer was
  // deleted, the latest message's `sourceIdentifier` is the only piece
  // of identity left in the system — it's whatever email / phone we
  // captured at ingestion time. We scan the most recent message
  // regardless of channel so this works for SMS threads too. May be an
  // empty string for messages we sent ourselves (which won't help, but
  // also won't crash).
  const fallbackIdentity = useMemo<string | null>(() => {
    for (let i = messageItems.length - 1; i >= 0; i -= 1) {
      const m = messageItems[i];
      if (m?.sourceIdentifier && m.sourceIdentifier.trim().length > 0) {
        return m.sourceIdentifier;
      }
    }
    return null;
  }, [messageItems]);

  // SMS replies stay on the peer number used in this conversation, which
  // can be a secondary contact number selected in the new-message composer.
  const recipientPhone =
    !isEmailThread && !isWebChat && fallbackIdentity?.startsWith('+')
      ? fallbackIdentity
      : customer?.primaryPhone;

  // Sender for the "Ignore" button. Prefer the customer's primary email
  // (stable across messages); fall back to the latest inbound's
  // sourceIdentifier (== from for inbound emails). Lowercased to match
  // the server's normalization so the "already blocked" check is a
  // simple string-equality compare.
  const senderForBlock = useMemo(() => {
    if (!isEmailThread) return null;
    const raw = customer?.primaryEmail ?? latestEmailMessage?.sourceIdentifier ?? null;
    return raw ? raw.trim().toLowerCase() : null;
  }, [isEmailThread, customer, latestEmailMessage]);
  const isAlreadyBlocked = useMemo(() => {
    if (!senderForBlock) return false;
    return isSenderBlocked(ignoreRules, senderForBlock);
  }, [senderForBlock, ignoreRules]);

  const emailReplyCtx = useMemo(() => {
    if (!isEmailThread || !latestEmailMessage || !customer) return null;
    const channelAccountId = latestEmailMessage.fromChannelAccountId ?? null;
    if (!channelAccountId) return null;
    const recipient = customer.primaryEmail ?? latestEmailMessage.sourceIdentifier ?? null;
    if (!recipient) return null;
    const baseSubject = (latestEmailMessage.subject ?? '').trim();
    const subject = baseSubject
      ? /^re:\s/i.test(baseSubject)
        ? baseSubject
        : `Re: ${baseSubject}`
      : '(no subject)';
    return {
      channelAccountId,
      contactId: customer.id,
      to: recipient,
      subject,
    };
  }, [isEmailThread, latestEmailMessage, customer]);

  // Reply permission on the thread's mailbox, from the accounts list (which
  // includes mailboxes shared with this user). Unknown until that row loads —
  // never assume a reply is allowed, or a view-only share briefly gets a
  // composer whose send the API rejects. A row without the flag comes from a
  // backend that predates sharing flags, where every listed mailbox could send.
  const replyMailbox = emailReplyCtx
    ? emailAccounts.find((a) => a.id === emailReplyCtx.channelAccountId)
    : undefined;
  const emailReplyAccess: 'unknown' | 'allowed' | 'view-only' = !replyMailbox
    ? 'unknown'
    : replyMailbox.canReply === false
      ? 'view-only'
      : 'allowed';

  const handleSendEmail = useCallback(
    async (payload: EmailComposerSendPayload) => {
      if (!emailReplyCtx) {
        toast.error('Cannot reply: missing mailbox or recipient context.');
        return;
      }
      const optimisticId = `opt-${Date.now()}`;
      const optimisticCreatedAt = new Date().toISOString();
      queryClient.setQueriesData<MessageCache>(
        { queryKey: [...messagingKeys.thread(threadId), 'messages'] },
        (old) => {
          if (!old) return old;
          return appendCachedMessage(old, {
            id: optimisticId,
            tenantId: '',
            threadId,
            customerId: emailReplyCtx.contactId,
            channel: 'EMAIL',
            direction: 'OUTBOUND',
            sourceIdentifier: payload.to,
            fromNumberId: null,
            senderUserId: null,
            body: payload.body,
            attachments: null,
            externalMediaUrl: null,
            deliveryStatus: 'QUEUED',
            errorCode: null,
            sentAt: optimisticCreatedAt,
            deliveredAt: null,
            readAt: null,
            createdAt: optimisticCreatedAt,
            fromChannelAccountId: emailReplyCtx.channelAccountId,
            subject: payload.subject,
            html: payload.html,
            emailAttachments: [],
          });
        },
      );
      try {
        const result = await sendEmailReply.mutateAsync({
          channelAccountId: emailReplyCtx.channelAccountId,
          payload: {
            contactId: emailReplyCtx.contactId,
            to: payload.to,
            subject: payload.subject,
            body: payload.body,
            html: payload.html,
            cc: payload.cc.length > 0 ? payload.cc : undefined,
            bcc: payload.bcc.length > 0 ? payload.bcc : undefined,
            attachments: payload.attachments.length > 0 ? payload.attachments : undefined,
          },
        });
        // See `handleSend` for the rationale. Same fix here so email replies
        // don't blink either — `EmailCard` also uses framer-motion with an
        // `initial` opacity/y, which replays on any key-driven remount.
        clientKeyForIdRef.current.set(result.messageId, optimisticId);
        queryClient.setQueriesData<MessageCache>(
          { queryKey: [...messagingKeys.thread(threadId), 'messages'] },
          (old) =>
            old
              ? mapCachedMessages(old, (m) =>
                  m.id === optimisticId
                    ? { ...m, id: result.messageId, deliveryStatus: 'SENT' }
                    : m,
                )
              : old,
        );
      } catch {
        toast.error('Failed to send email reply. Please try again.');
        throw new Error('send-failed'); // signal failure to the composer
      }
    },
    [emailReplyCtx, threadId, queryClient, sendEmailReply],
  );

  // Header identity. The thread/customer pipeline gives us a few
  // overlapping signals; collapse them here so the JSX below stays
  // legible:
  //   - `customer` resolved AND not the `(unknown)` placeholder    → real contact
  //   - `customer` resolved as `(unknown)` OR `isContactDeleted`   → orphan thread
  //   - neither (still loading)                                     → "Loading…"
  // For the orphan case we fall back to the latest message's
  // sourceIdentifier (email or phone) so the operator still has SOME
  // identity for the conversation rather than a meaningless "(unknown)".
  const hasResolvedContact =
    !isGroup &&
    Boolean(
      customer && !isContactDeleted && customer.displayName && customer.displayName !== '(unknown)',
    );
  const headerDisplayName = isGroup
    ? thread?.title?.trim() || 'Messaging group'
    : hasResolvedContact
      ? customer!.displayName
      : isContactDeleted || threadQuery.data
        ? (thread?.senderIdentifier ?? fallbackIdentity ?? 'Removed contact')
        : 'Loading…';
  const headerInitial = hasResolvedContact ? customer!.displayName.charAt(0).toUpperCase() : null;

  // Replies are blocked when the underlying Contact row is gone. The
  // SMS endpoint refuses sends without a real customerId, and the email
  // endpoint requires `contactId` for sender-resolution + threading —
  // both would 4xx if we tried, so disable the composer up-front and
  // show the operator a clear notice.
  const repliesDisabled = receiveOnly || isClosed || (!isGroup && isContactDeleted);

  return (
    <div className="flex min-h-0 flex-1">
      <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
        {/* Header */}
        <div className="flex h-12 shrink-0 items-center justify-between gap-2 border-b px-4">
          <div className="flex items-center gap-3 min-w-0">
            <div
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xs font-medium',
                hasResolvedContact || isGroup
                  ? 'bg-primary/10 text-primary'
                  : 'bg-muted text-muted-foreground',
              )}
            >
              {isGroup ? (
                <Users className="h-3.5 w-3.5" aria-hidden />
              ) : (
                (headerInitial ?? <UserX className="h-3.5 w-3.5" aria-hidden />)
              )}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-medium flex items-center gap-1.5">
                {customer && !isContactDeleted && !isGroup ? (
                  <button
                    type="button"
                    onClick={() =>
                      void navigate({
                        to: '/dashboard/contacts/$contactId',
                        params: { contactId: customer.id },
                      })
                    }
                    className="truncate text-left hover:underline focus:underline focus:outline-none"
                    title="View customer"
                  >
                    {headerDisplayName}
                  </button>
                ) : (
                  headerDisplayName
                )}
                {isContactDeleted && (
                  <span
                    className="inline-flex items-center rounded-sm bg-amber-100 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-amber-800"
                    title="The contact this conversation was attached to has been removed"
                  >
                    Removed
                  </span>
                )}
                {isContactSoftDeleted && (
                  <span
                    className="inline-flex items-center rounded-sm bg-amber-100 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-amber-800"
                    title="This contact was deleted — open it to restore"
                  >
                    Deleted
                  </span>
                )}
                {isGroup ? (
                  <span className="inline-flex items-center rounded-sm bg-violet-100 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-violet-800">
                    Group
                  </span>
                ) : null}
                {recipientPhone && tenantPhoneSet.has(recipientPhone) && (
                  // The "customer" on this thread is actually one of OUR
                  // Telnyx lines — typically a platform-admin test SMS
                  // landed here. Flag it so the operator doesn't try to
                  // reply assuming it's a real customer.
                  <span
                    className="inline-flex items-center rounded-sm bg-amber-100 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-amber-800"
                    title="This conversation came from one of this tenant's own phone numbers"
                  >
                    Internal
                  </span>
                )}
              </p>
              <div className="flex items-center gap-2 text-[10px] text-muted-foreground">
                {/* Channel pill — explicit so operators can tell at a glance
                  which medium this thread is on. v1 is SMS-only in
                  practice, but the schema already supports MMS / EMAIL /
                  CALL / etc. — render whatever the most-recent message
                  reports, defaulting to SMS when the thread is empty. */}
                {(() => {
                  // Thread-level `channel` (see ThreadDetail) is derived
                  // server-side from last-message-channel + the
                  // EMAIL_SENTINEL phoneNumberId fallback, so empty email
                  // threads render EMAIL instead of falling back to SMS.
                  const channel = thread?.channel ?? thread?.lastMessage?.channel ?? 'SMS';
                  return (
                    <span
                      className="inline-flex items-center rounded-sm bg-sky-100 px-1 py-px text-[9px] font-bold uppercase tracking-wider text-sky-800"
                      title={`Channel: ${channel}`}
                    >
                      {channel}
                    </span>
                  );
                })()}
                {recipientPhone && (
                  <span className="flex items-center gap-1">
                    <Phone className="h-2.5 w-2.5" />
                    {recipientPhone}
                  </span>
                )}
                {isGroup && thread?.groupParticipants.length ? (
                  <span
                    className="flex min-w-0 items-center gap-1"
                    title={thread.groupParticipants
                      .map(
                        (participant) =>
                          `${participant.displayName ?? participant.e164} (${participant.e164})`,
                      )
                      .join(', ')}
                  >
                    <Users className="h-2.5 w-2.5 shrink-0" />
                    <span className="truncate">
                      {thread.groupParticipants
                        .map((participant) => participant.displayName ?? participant.e164)
                        .join(', ')}
                    </span>
                  </span>
                ) : null}
                {/* "Your line" — the tenant phone this thread is on. Backend-derived
                  from the most-recent message with a non-null fromNumberId.
                  ALWAYS shown when present (including intra-account threads where
                  the line happens to match the contact's number — in that case it
                  confirms the loopback is intentional rather than hiding it). The
                  only case it's hidden is `null`: a thread whose only history
                  predates the inbound-stamp ingestion change. */}
                {thread?.tenantLineE164 && (
                  <span
                    className="flex items-center gap-1"
                    title="The tenant line this conversation is on (you)"
                  >
                    <span className="opacity-60">→ via</span>
                    <span className="font-medium text-foreground/80">{thread.tenantLineE164}</span>
                  </span>
                )}
                {/* "Your mailbox" — email analogue of the tenantLineE164 chip.
                  Derived from the active email thread's latest message's
                  fromChannelAccountId, looked up in the email-accounts
                  cache to render the actual address (e.g. sales@ vs
                  support@). Renders only on email threads where we know
                  which mailbox received the conversation. */}
                {isEmailThread &&
                  emailReplyCtx?.channelAccountId &&
                  (() => {
                    const mailbox = emailAccounts.find(
                      (a) => a.id === emailReplyCtx.channelAccountId,
                    );
                    if (!mailbox) return null;
                    return (
                      <span
                        className="flex items-center gap-1"
                        title="The connected mailbox this conversation is on (you)"
                      >
                        <span className="opacity-60">→ via</span>
                        <span className="font-medium text-foreground/80">
                          {mailbox.emailAddress}
                        </span>
                      </span>
                    );
                  })()}
                {thread?.status === 'SNOOZED' && (
                  <span className="flex items-center gap-1">
                    <Clock className="h-2.5 w-2.5" />
                    Snoozed
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {recipientPhone && !isGroup ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => callNumber(recipientPhone)}
                title={`Call ${recipientPhone}`}
              >
                <Phone className="mr-1.5 h-3.5 w-3.5" />
                Call
              </Button>
            ) : null}
            {isEmailThread && senderForBlock && !isAlreadyBlocked && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  const ok = window.confirm(
                    `Block ${senderForBlock}? Any future emails from this sender will be dropped at ingestion — no thread, no notification. You can unblock from Settings → Email accounts.`,
                  );
                  if (!ok) return;
                  blockSender.mutate(senderForBlock, {
                    onSuccess: () => {
                      // Close the current thread once the sender is on the
                      // ignore list, so the inbox view matches the new
                      // "we're ignoring you" state.
                      closeThread.mutate(undefined);
                    },
                  });
                }}
                disabled={blockSender.isPending}
                title="Block this sender from future inbound email"
              >
                <Ban className="mr-1.5 h-3.5 w-3.5" />
                Ignore sender
              </Button>
            )}
            {/* Per-conversation AI takeover — shown only when the channel has a
              bot assigned. Off = operator has taken over (bot paused for this
              thread); On = the bot auto-replies. */}
            {/* Three states, each visually distinct: Iris replying, an operator
              paused Iris, or Iris escalated the conversation itself. The
              last two both mean "a person owns this thread", but only the
              escalation was Iris asking for help — it must not read like
              something the operator did. */}
            {thread?.aiReceptionist &&
              !isClosed &&
              (() => {
                const escalated = Boolean(thread.escalation);
                const paused = Boolean(thread.aiPaused);
                const title = escalated
                  ? 'Iris escalated this conversation and is paused. Click to resume Iris.'
                  : paused
                    ? "You've taken over — Iris is paused for this conversation. Click to hand it back."
                    : 'Iris is replying to this conversation. Click to take over.';
                return (
                  <Button
                    size="sm"
                    variant={paused ? 'outline' : 'secondary'}
                    className={cn(escalated && 'border-red-300 text-red-800 hover:bg-red-50')}
                    onClick={() => setThreadAi.mutate(paused)}
                    disabled={setThreadAi.isPending}
                    title={title}
                  >
                    {escalated ? (
                      <>
                        <AlertTriangle className="mr-1.5 h-3.5 w-3.5" />
                        Escalated
                      </>
                    ) : paused ? (
                      <>
                        <BotOff className="mr-1.5 h-3.5 w-3.5" />
                        Paused by you
                      </>
                    ) : (
                      <>
                        <Bot className="mr-1.5 h-3.5 w-3.5" />
                        Iris on
                      </>
                    )}
                  </Button>
                );
              })()}
            {thread?.otherEnabled && thread.phoneNumberId && !isGroup && (
              <Button
                size="sm"
                variant="outline"
                disabled={setThreadSection.isPending}
                onClick={() =>
                  setThreadSection.mutate(thread.section === 'OTHER' ? 'INBOX' : 'OTHER')
                }
              >
                {thread.section === 'OTHER' ? 'Move to inbox' : 'Move to Other'}
              </Button>
            )}
            {isClosed ? (
              <Button
                size="sm"
                variant="outline"
                onClick={() => reopenThread.mutate()}
                disabled={reopenThread.isPending}
              >
                <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                Reopen
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                onClick={() => closeThread.mutate(undefined)}
                disabled={closeThread.isPending}
              >
                Close
              </Button>
            )}
          </div>
        </div>

        {thread?.escalation &&
          !isClosed &&
          (!thread.section || thread.section === 'INBOX') &&
          !receiveOnly && (
            <EscalationBanner
              escalation={thread.escalation}
              assignedToMe={Boolean(currentUserId) && thread.assignedUserId === currentUserId}
              canClaim={Boolean(currentUserId)}
              claimPending={reassignThread.isPending}
              resumePending={setThreadAi.isPending}
              onClaim={() => {
                if (!currentUserId) return;
                reassignThread.mutate({ toUserId: currentUserId });
              }}
              onResume={() => setThreadAi.mutate(true)}
            />
          )}
        <MessageThread
          messages={messages}
          isLoading={messagesQuery.isLoading}
          isFetchingNextPage={messagesQuery.isFetchingNextPage}
          hasNextPage={messagesQuery.hasNextPage}
          fetchNextPage={() => void messagesQuery.fetchNextPage()}
          isTyping={isBotThinking}
        />
        {/*
        Channel-aware composer routing. The SMS endpoint
        (`POST /api/messaging/inbox/threads/:id/messages`) requires a
        valid PhoneNumber row backing the thread — email threads carry
        the email-sentinel `phoneNumberId` from #404 rather than a real
        PhoneNumber row, so the SMS endpoint cannot resolve them. For email
        we route through the connected-mailbox endpoint
        (`POST /messaging/email/accounts/:channelAccountId/messages`),
        carrying contact + subject + body. Detection uses the thread's
        most-recent message channel — the same field that drives the
        SMS / Email pill in the header above. Empty threads default to
        SMS (newly-created from the contacts UI).
      */}
        {receiveOnly ? (
          <div
            className="border-t bg-muted/30 px-4 py-3 text-sm text-muted-foreground"
            role="status"
          >
            Receive only — messages from this sender are saved here, but replies and calls are
            unavailable.
          </div>
        ) : isContactDeleted ? (
          <RemovedContactNotice fallbackIdentity={fallbackIdentity} />
        ) : isEmailThread ? (
          emailReplyCtx && emailReplyAccess === 'view-only' ? (
            // A view-only share: the conversation is readable, but sending
            // from this mailbox is refused server-side — don't offer it.
            <div className="border-t bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
              View only — this mailbox is shared with you without reply access. Ask its owner for
              “Can view &amp; reply” to answer here.
            </div>
          ) : emailReplyCtx && emailReplyAccess === 'allowed' ? (
            <EmailMessageComposer
              initial={{ to: emailReplyCtx.to, subject: emailReplyCtx.subject }}
              onSend={handleSendEmail}
              signature={(() => {
                // Per-mailbox signature wins when its enable toggle is on
                // and content exists. Otherwise fall back to the user-level
                // signature so existing #437 setups still work.
                const mailbox = emailAccounts.find((a) => a.id === emailReplyCtx.channelAccountId);
                if (mailbox?.signatureEnabled && (mailbox.signatureHtml || mailbox.signatureText)) {
                  return {
                    html: mailbox.signatureHtml,
                    text: mailbox.signatureText,
                  };
                }
                return {
                  html: myProfile?.emailSignatureHtml ?? null,
                  text: myProfile?.emailSignatureText ?? null,
                };
              })()}
              disabled={repliesDisabled}
            />
          ) : (
            <div className="border-t bg-muted/40 px-4 py-3 text-xs text-muted-foreground">
              Loading reply context — wait for the latest email to load, or check that this contact
              has an email address on file.
            </div>
          )
        ) : (
          <>
            {!isGroup && !isWebChat && (senderLines.data?.length ?? 0) > 1 ? (
              <div className="flex items-center gap-2 border-t px-4 py-2 text-xs">
                <label htmlFor="inbox-sending-number" className="shrink-0 text-muted-foreground">
                  Send from
                </label>
                <Select
                  value={threadPhoneNumberId ?? undefined}
                  onValueChange={(id) => void changeSenderLine(id)}
                >
                  <SelectTrigger id="inbox-sending-number" className="h-8 max-w-xs">
                    <SelectValue placeholder="Select a number" />
                  </SelectTrigger>
                  <SelectContent>
                    {senderLines.data?.map((line) => (
                      <SelectItem key={line.id} value={line.id}>
                        {line.label || line.e164} · {line.e164}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <MessageComposer
              onSend={handleSend}
              // Web-chat threads have no media transport — hide attachments.
              onUploadAttachment={isWebChat ? undefined : handleUpload}
              disabled={repliesDisabled}
            />
          </>
        )}
      </div>
      {isWebChat && <VisitorContextPanel ctx={thread?.visitorContext} />}
    </div>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyState() {
  return (
    <div className="flex flex-1 flex-col items-center justify-center gap-2 text-muted-foreground">
      <Search className="h-10 w-10 opacity-20" />
      <p className="text-sm">Select a conversation to view messages</p>
    </div>
  );
}

// ─── Permission banner ────────────────────────────────────────────────────────

function PermissionBanner({
  onEnable,
  onDismiss,
}: {
  onEnable: () => void | Promise<void>;
  onDismiss: () => void;
}) {
  return (
    <div
      role="region"
      aria-label="Desktop notification prompt"
      className="m-2 rounded-md border border-primary/20 bg-primary/5 px-3 py-2 flex items-start gap-2"
    >
      <Bell className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary" aria-hidden />
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium leading-tight">Get desktop notifications</p>
        <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
          Stay on top of new messages when this tab is in the background.
        </p>
        <div className="mt-1.5 flex items-center gap-1.5">
          <Button
            size="xs"
            onClick={() => {
              void onEnable();
            }}
          >
            Enable
          </Button>
          <Button size="xs" variant="ghost" onClick={onDismiss}>
            Not now
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss notification prompt"
        className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}
